import { useTranslation } from 'next-i18next'
import styled from 'styled-components'

import ENV_VARS from '@/config'
import { breakpoints, colors, fontSizes } from '@/styles'
import emailIcon from '@/features/Company/icons/email-icon.png'
import globeIcon from '@/features/Company/icons/globe-icon.png'
import markerIcon from '@/features/Company/icons/marker-icon.png'
import Head from '@/components/head'

import heroBanner from './carreramanagementcorporation.jpg'
import titleLogo from './carreramanagementcorporationlogo.png'
import carreraQr from './carreraqr.png'

const { BASE_URL } = ENV_VARS

const BannerWrapper = styled.div`
  margin-bottom: 30px;
  height: 420px;
  overflow: hidden;
  position: relative;

  @media only screen and (max-width: ${breakpoints.phoneMax}) {
    height: 240px;
  }
`

const Container = styled.div`
  height: 100%;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`

const SectionTitle = styled.h3`
  color: ${colors.regular};
  font-size: ${fontSizes.medium};
  font-weight: bold;
  letter-spacing: -0.49px;
  line-height: 22px;
  text-align: center;
  margin-bottom: 30px;

  @media screen and (max-width: 700px) {
    display: none;
  }
`

const TopContentWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
`

const TopContent = styled.div`
  display: flex;
  max-width: 1096px;
  margin: 20px 20px 40px 20px;
  font-family: var(--font-open-sans, 'Open Sans', sans-serif);
  @media only screen and (max-width: ${breakpoints.ipadMiniMax}) {
    flex-direction: column;
  }
`

const TextBlock = styled.div`
  display: flex;
  flex-direction: column;
  margin-right: 48px;
`

const TopCompanyTitle = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 32px;
`

const TitleLogo = styled.img`
  margin-right: 12px;
  width: 66px;
  height: 32px;
`

const CompanyTitle = styled.h3`
  color: ${colors.black};
  font-size: 32px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  margin: 0;
`

const TopText = styled.p`
  color: #000;
  font-family: var(--font-open-sans, 'Open Sans', sans-serif);
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
`

const BoldText = styled(TopText)`
  font-weight: 700;
`

const MansionTitle = styled(BoldText)`
  margin-top: 2px;
`

const Video = styled.video`
  display: block;
  position: absolute;
  left: 0;
  min-height: 100%;
  min-width: 100%;
  z-index: 0;

  @media only screen and (max-width: ${breakpoints.phoneMax}) {
    min-height: unset;
    height: 250px;
  }
`

const QrBlock = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  height: min-content;
  padding: 16px 24px;
  border-radius: 8px;
  background-color: #f3f5f7;

  @media only screen and (max-width: ${breakpoints.tabletLandscape}) {
    flex-direction: column;
  }

  @media only screen and (max-width: ${breakpoints.ipadMiniMax}) {
    margin-top: 15px;
  }
`

const LinksContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: 20px;
`

const LinkContainer = styled.div`
  display: flex;
  align-items: center;
  width: max-content;
`

const LinkLogo = styled.img`
  margin-right: 6px;
  width: 16px;
  height: 16px;
`

const Link = styled.a`
  color: #000;
  font-family: var(--font-open-sans, 'Open Sans', sans-serif);
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  text-decoration: none;
`

const QrImageContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 115px;
`

const QrImage = styled.img`
  width: 98px;
  height: 98px;
`

const CarreraManagementCorporation = ({ metadata }) => {
  const { t } = useTranslation('carreramanagementcorporation')
  const profile = {
    banner: heroBanner.src,
    title: t('carreramanagementcorporation:Carrera Management Corporation'),
    pageDescription: t('carreramanagementcorporation:description.meta'),
    url: '/company/carreramanagementcorporation'
  }

  return (
    <>
      <Head
        title={`${profile.title} | liv.rent`}
        description={profile.pageDescription}
        meta={{
          image: `${BASE_URL}/images/carreramanagementcorporationbanner.jpg`
        }}
        path={profile.url}
        pageData={metadata}
        hideCustomerSupport
      />
      <BannerWrapper background={profile.banner}>
        <Container>
          <Video autoPlay loop muted>
            <source src="/videos/carrera-banner.mp4" type="video/mp4" />
          </Video>
        </Container>
      </BannerWrapper>
      <TopContentWrapper>
        <TopContent>
          <TextBlock>
            <TopCompanyTitle>
              <TitleLogo
                src={titleLogo.src}
                alt="Carrera Management Corporation"
              />
              <CompanyTitle>Carrera Management Corporation</CompanyTitle>
            </TopCompanyTitle>
            <BoldText>{t('carreramanagementcorporation:subtitle')}</BoldText>
            <TopText>{t('carreramanagementcorporation:description')}</TopText>
          </TextBlock>
          <QrBlock>
            <QrImageContainer>
              <QrImage
                alt="Carrera Management Corporation QR Code"
                src={carreraQr.src}
              />
              <MansionTitle>The Mansion</MansionTitle>
            </QrImageContainer>
            <LinksContainer>
              <LinkContainer>
                <LinkLogo src={globeIcon.src} alt="" />
                <Link
                  href="https://carreramc.com/themansion"
                  rel="noreferrer noopener"
                  target="_blank"
                >
                  www.carreramc.com/themansion
                </Link>
              </LinkContainer>
              <LinkContainer>
                <LinkLogo src={emailIcon.src} alt="" />
                <Link
                  target="_blank"
                  href="mailto:themansion@carreramc.com"
                  rel="noreferrer noopener"
                >
                  themansion@carreramc.com
                </Link>
              </LinkContainer>
              <LinkContainer>
                <LinkLogo src={markerIcon.src} alt="" />
                <Link
                  target="_blank"
                  href="https://maps.app.goo.gl/yodpn7v2MB1kU6SPA"
                  rel="noreferrer noopener"
                >
                  1523 Davie St, Vancouver
                </Link>
              </LinkContainer>
            </LinksContainer>
          </QrBlock>
        </TopContent>
      </TopContentWrapper>
      <SectionTitle>
        {t('carreramanagementcorporation:Our Portfolio')}
      </SectionTitle>
    </>
  )
}

export default CarreraManagementCorporation
